import React, { useRef, useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { api as afriApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Upload, Video, X, ShoppingBag, Sparkles, Scissors, Wand2, Hash, Image as ImageIcon, Camera } from "lucide-react";
import { toast } from "sonner";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";
import PlyxLiveRecorder from "@/components/plyx/PlyxLiveRecorder";

const MAX_MB = 250;
const MAX_DURATION = 180;

// CSS filter presets — the player applies the same `filter: ...` style
// at playback time so what the creator sees is what viewers see.
// v2 expansion: 10 face-beauty presets on top of the original 6 (the
// codes mirror PLYX_BEAUTIFY_FILTERS in /app/backend/routes/plyx_videos.py).
const BEAUTIFY_FILTERS = [
  { code: "none",       label: "None",       group: "Plain",  css: "none" },
  // Originals
  { code: "vivid",      label: "Vivid",      group: "Plain",  css: "saturate(1.4) contrast(1.1)" },
  { code: "warm",       label: "Warm",       group: "Plain",  css: "sepia(0.18) saturate(1.2) hue-rotate(-8deg)" },
  { code: "cool",       label: "Cool",       group: "Plain",  css: "saturate(1.1) hue-rotate(8deg) brightness(1.02)" },
  { code: "bw",         label: "B&W",        group: "Plain",  css: "grayscale(1) contrast(1.05)" },
  { code: "vintage",    label: "Vintage",    group: "Plain",  css: "sepia(0.5) saturate(0.85) contrast(0.95)" },
  { code: "dreamy",     label: "Dreamy",     group: "Plain",  css: "blur(0.4px) saturate(1.15) brightness(1.05) contrast(0.95)" },
  // Face-beauty v2
  { code: "smooth",     label: "Smooth",     group: "Beauty", css: "blur(0.7px) saturate(1.1) brightness(1.06) contrast(0.96)" },
  { code: "glow",       label: "Glow",       group: "Beauty", css: "brightness(1.12) saturate(1.18) contrast(1.05) hue-rotate(-4deg)" },
  { code: "porcelain",  label: "Porcelain",  group: "Beauty", css: "brightness(1.1) saturate(0.9) contrast(1.02) hue-rotate(4deg)" },
  { code: "kbeauty",    label: "K-Beauty",   group: "Beauty", css: "brightness(1.08) saturate(1.05) contrast(0.94) sepia(0.06)" },
  { code: "cinematic",  label: "Cinematic",  group: "Beauty", css: "contrast(1.18) saturate(0.92) brightness(0.97)" },
  { code: "sunset",     label: "Sunset",     group: "Beauty", css: "sepia(0.25) saturate(1.25) brightness(1.05) hue-rotate(-12deg)" },
  { code: "golden",     label: "Golden",     group: "Beauty", css: "sepia(0.45) saturate(1.3) brightness(1.06)" },
  { code: "fresh",      label: "Fresh",      group: "Beauty", css: "brightness(1.1) saturate(1.3) contrast(1.04)" },
  { code: "midnight",   label: "Midnight",   group: "Beauty", css: "brightness(0.92) saturate(1.05) contrast(1.1) hue-rotate(14deg)" },
  { code: "drama",      label: "Drama",      group: "Beauty", css: "contrast(1.3) saturate(0.7) brightness(0.96)" },
];
const FILTERS_BY_CODE = Object.fromEntries(BEAUTIFY_FILTERS.map(f => [f.code, f]));

// Curated sticker palette grouped by vibe.
const STICKER_PALETTE = [
  // Faces & sparkle
  "✨", "💫", "⭐", "🌟", "💖", "💗", "💕", "💋", "😍", "🥰", "🤩",
  // Reactions
  "🔥", "💯", "🙌", "👏", "😂", "😎", "🤯", "💀", "🤝",
  // Crowns & trophies
  "👑", "🏆", "🎖️", "🥇", "💎", "🎉", "🎊",
  // Africa & motion
  "🌍", "🌴", "🌺", "🌸", "🎶", "🥁", "🎵",
  // Animals/luck
  "🦋", "🐉", "🦁", "🦄", "🐝", "🐢",
  // Food & home
  "🍔", "🌶️", "🍯", "🍫", "🥃", "🌹",
];

// Extract a single-frame thumbnail from a local video file using canvas.
// Returns a Blob (image/jpeg) or null on failure.
const extractThumbnail = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    v.muted = true;
    v.playsInline = true;
    v.crossOrigin = "anonymous";
    v.addEventListener("loadeddata", () => {
      try { v.currentTime = Math.min(0.5, (v.duration || 1) * 0.1); } catch { /* ignore */ }
    });
    v.addEventListener("seeked", () => {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 720;
      canvas.height = v.videoHeight || 1280;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        resolve(blob || null);
      }, "image/jpeg", 0.8);
    });
    v.addEventListener("error", () => { URL.revokeObjectURL(url); resolve(null); });
  } catch { resolve(null); }
});

const getVideoDuration = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    v.addEventListener("loadedmetadata", () => {
      const d = v.duration;
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(d) ? d : 0);
    });
    v.addEventListener("error", () => { URL.revokeObjectURL(url); resolve(0); });
  } catch { resolve(0); }
});

const PlyxUpload = () => {
  const navigate = useNavigate();
  const { user: plyxUser, loading: plyxLoading } = usePlyxAuth();
  const { user: afriUser } = useAuth();
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);
  const photoFileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(null);
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState("");
  const [myListings, setMyListings] = useState([]);
  const [listingId, setListingId] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [beautifyFilter, setBeautifyFilter] = useState("none");
  const [stickers, setStickers] = useState([]);   // [{emoji,x,y,size,rot}]
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [aiBusy, setAiBusy] = useState(null);     // "polish" | "tags" | null
  const [mediaType, setMediaType] = useState("video"); // "video" | "photo"
  const [liveRecorderOpen, setLiveRecorderOpen] = useState(false);
  const previewBoxRef = useRef(null);

  // Load this AfriMarket user's listings so they can link one to the video.
  useEffect(() => {
    if (!afriUser) return;
    afriApi.get("/listings", { params: { owner_id: afriUser.user_id, approval: "all", limit: 100 } })
      .then((r) => setMyListings(r.data || []))
      .catch(() => { /* optional */ });
  }, [afriUser]);

  if (plyxLoading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!plyxUser) {
    return <Navigate to="/plyx/login" replace state={{ from: "/plyx/upload" }}/>;
  }

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await acceptFile(f);
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setMediaType("video");
    setDuration(null);
    setProgress(0);
    setBeautifyFilter("none");
    setStickers([]);
    setTrimStart(0);
    setTrimEnd(0);
    setAiBusy(null);
    if (fileRef.current) fileRef.current.value = "";
    if (videoFileRef.current) videoFileRef.current.value = "";
    if (photoFileRef.current) photoFileRef.current.value = "";
  };

  // Accept a File from any source (file picker OR live AR recorder).
  const acceptFile = async (f) => {
    const isVideo = f.type.startsWith("video/");
    const isImage = f.type.startsWith("image/");
    if (!isVideo && !isImage) {
      toast.error("Please choose a video or photo file");
      return;
    }
    const mb = f.size / (1024 * 1024);
    const maxMb = isImage ? 30 : MAX_MB;
    if (mb > maxMb) {
      toast.error(`File is ${mb.toFixed(0)} MB — max ${maxMb} MB`);
      return;
    }
    let dur = null;
    if (isVideo) {
      dur = await getVideoDuration(f);
      if (dur > MAX_DURATION + 1) {
        toast.error(`Video is ${dur.toFixed(0)}s — max ${MAX_DURATION}s`);
        return;
      }
    }
    setFile(f);
    setMediaType(isImage ? "photo" : "video");
    setDuration(dur);
    setTrimStart(0);
    setTrimEnd(dur || 0);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(1);
    try {
      const isPhoto = mediaType === "photo";
      const fd = new FormData();
      fd.append("video", file);
      // Only extract a poster thumbnail for videos. Photos are their own thumbnail.
      if (!isPhoto) {
        const thumb = await extractThumbnail(file);
        if (thumb) fd.append("thumbnail", new File([thumb], "thumb.jpg", { type: "image/jpeg" }));
      }
      fd.append("caption", caption);
      if (listingId) fd.append("listing_id", listingId);
      if (tags) fd.append("tags", tags);
      if (!isPhoto && duration) fd.append("duration_sec", String(duration));
      if (beautifyFilter && beautifyFilter !== "none") fd.append("beautify_filter", beautifyFilter);
      if (stickers.length > 0) fd.append("stickers", JSON.stringify(stickers));
      // Only send trim if the user actually narrowed the clip (videos only).
      if (!isPhoto && duration && (trimStart > 0.05 || trimEnd < duration - 0.05)) {
        fd.append("trim_start", String(trimStart));
        fd.append("trim_end", String(trimEnd));
      }
      const { data } = await plyxApi.post("/plyx/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success(
        isPhoto
          ? "Posted to Plyx · photo is live."
          : "Posted to Plyx · auto-captions running in the background",
        { duration: 4000 }
      );
      navigate(`/plyx/u/${plyxUser.handle}`);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-upload-page">
      {liveRecorderOpen && (
        <PlyxLiveRecorder
          onCancel={() => setLiveRecorderOpen(false)}
          onCapture={(f) => {
            setLiveRecorderOpen(false);
            acceptFile(f).then(() => toast.success("Live recording captured — review and post."));
          }}
        />
      )}
      <div className="container-narrow py-12">
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">Upload to Plyx</h1>
        <p className="text-white/60 mb-8">Videos up to {MAX_MB}MB · {MAX_DURATION}s · MP4 / MOV / WebM &nbsp;·&nbsp; Photos up to 30MB · JPG / PNG / WebP</p>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Picker / preview */}
          <div>
            {!previewUrl ? (
              <div className="space-y-3">
                <div className="w-full aspect-[9/16] max-h-[520px] rounded-2xl border-2 border-dashed border-white/15 hover:border-[#FF2D7A] transition-colors flex flex-col items-center justify-center bg-white/5 px-6">
                  <div className="flex items-center gap-3 mb-4 text-white/40">
                    <Video className="w-8 h-8"/>
                    <span className="text-xs uppercase tracking-wider">or</span>
                    <ImageIcon className="w-8 h-8"/>
                  </div>
                  <div className="font-semibold text-center mb-1">Choose what to upload</div>
                  <div className="text-xs text-white/40 text-center mb-5">Video up to {MAX_MB}MB · Photo up to 30MB</div>
                  <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
                    <button
                      type="button"
                      onClick={() => videoFileRef.current?.click()}
                      data-testid="plyx-upload-pick-video"
                      className="px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/15 inline-flex items-center justify-center gap-1.5"
                    >
                      <Video className="w-4 h-4"/> Video
                    </button>
                    <button
                      type="button"
                      onClick={() => photoFileRef.current?.click()}
                      data-testid="plyx-upload-pick-photo"
                      className="px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/15 inline-flex items-center justify-center gap-1.5"
                    >
                      <ImageIcon className="w-4 h-4"/> Photo
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setLiveRecorderOpen(true)}
                  data-testid="plyx-upload-open-live-ar"
                  className="w-full px-4 py-3 rounded-full font-semibold text-[#0A0A0A] inline-flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
                >
                  <Camera className="w-4 h-4"/> Record live with AR face filter
                </button>
              </div>
            ) : (
              <div ref={previewBoxRef} className="relative aspect-[9/16] max-h-[520px] rounded-2xl overflow-hidden bg-black">
                {mediaType === "photo" ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    data-testid="plyx-upload-preview-photo"
                    style={{ filter: FILTERS_BY_CODE[beautifyFilter]?.css || "none" }}
                  />
                ) : (
                  <video
                    src={previewUrl}
                    controls
                    className="w-full h-full"
                    data-testid="plyx-upload-preview"
                    style={{ filter: FILTERS_BY_CODE[beautifyFilter]?.css || "none" }}
                  />
                )}
                {/* Sticker overlay on preview — exact same positioning the player will use */}
                <div className="absolute inset-0 pointer-events-none">
                  {stickers.map((s, i) => (
                    <span
                      key={i}
                      className="absolute pointer-events-auto select-none cursor-pointer"
                      onDoubleClick={() => setStickers((arr) => arr.filter((_, j) => j !== i))}
                      title="Double-click to remove"
                      style={{
                        left: `${s.x * 100}%`,
                        top: `${s.y * 100}%`,
                        fontSize: s.size,
                        transform: `translate(-50%, -50%) rotate(${s.rot}deg)`,
                        textShadow: "0 0 8px rgba(0,0,0,0.4)",
                      }}
                    >
                      {s.emoji}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="plyx-upload-clear"
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80"
                  aria-label="Choose different file"
                >
                  <X className="w-4 h-4"/>
                </button>
                <div className="absolute bottom-3 left-3 px-2 py-1 rounded bg-black/60 backdrop-blur text-[10px] uppercase tracking-wider">
                  {mediaType === "photo"
                    ? `Photo · ${(file.size / (1024 * 1024)).toFixed(1)}MB`
                    : `${(duration || 0).toFixed(1)}s · ${(file.size / (1024 * 1024)).toFixed(1)}MB`}
                </div>
              </div>
            )}
            <input ref={videoFileRef} type="file" accept="video/*" hidden onChange={onPick} data-testid="plyx-upload-video-input"/>
            <input ref={photoFileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="plyx-upload-photo-input"/>
            <input ref={fileRef} type="file" accept="video/*,image/*" hidden onChange={onPick} data-testid="plyx-upload-file-input"/>

            {/* Beautify panel — only shown once a video is loaded */}
            {previewUrl && (
              <div className="mt-4 space-y-4" data-testid="plyx-upload-beautify-panel">
                {["Beauty", "Plain"].map((group) => (
                  <div key={group}>
                    <label className="block text-xs uppercase tracking-wider text-white/60 mb-2 flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#FFB42D]"/>
                      {group === "Beauty" ? "Face-beauty filters" : "Classic filters"}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {BEAUTIFY_FILTERS.filter(f => f.group === group).map((f) => (
                        <button
                          key={f.code}
                          type="button"
                          onClick={() => setBeautifyFilter(f.code)}
                          data-testid={`plyx-upload-filter-${f.code}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                                      ${beautifyFilter === f.code
                                        ? "bg-white text-[#0A0A0A] border-white"
                                        : "border-white/20 text-white/80 hover:border-white/40"}`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Trim — choose start & end seconds (videos only) */}
                {mediaType === "video" && duration && (
                  <div data-testid="plyx-upload-trim-panel">
                    <label className="block text-xs uppercase tracking-wider text-white/60 mb-2 flex items-center gap-2">
                      <Scissors className="w-3.5 h-3.5 text-[#00E0C7]"/> Trim
                      <span className="ml-auto text-[10px] text-white/40 normal-case">
                        {`${trimStart.toFixed(1)}s → ${trimEnd.toFixed(1)}s · ${(trimEnd - trimStart).toFixed(1)}s clip`}
                      </span>
                    </label>
                    <div className="space-y-2">
                      <div>
                        <div className="text-[10px] text-white/50 mb-1">Start</div>
                        <input
                          type="range"
                          min={0}
                          max={Math.max(0.1, duration - 0.1)}
                          step={0.1}
                          value={trimStart}
                          onChange={(e) => {
                            const v = Math.min(parseFloat(e.target.value), trimEnd - 0.5);
                            setTrimStart(Math.max(0, v));
                          }}
                          data-testid="plyx-upload-trim-start"
                          className="w-full accent-[#00E0C7]"
                        />
                      </div>
                      <div>
                        <div className="text-[10px] text-white/50 mb-1">End</div>
                        <input
                          type="range"
                          min={0.5}
                          max={duration}
                          step={0.1}
                          value={trimEnd}
                          onChange={(e) => {
                            const v = Math.max(parseFloat(e.target.value), trimStart + 0.5);
                            setTrimEnd(Math.min(duration, v));
                          }}
                          data-testid="plyx-upload-trim-end"
                          className="w-full accent-[#FF2D7A]"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Emoji stickers */}
                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <label className="text-xs uppercase tracking-wider text-white/60">Emoji stickers ({stickers.length}/12)</label>
                    {stickers.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setStickers([])}
                        data-testid="plyx-upload-stickers-clear"
                        className="text-[11px] text-white/50 hover:text-white"
                      >Clear all</button>
                    )}
                  </div>
                  <div className="grid grid-cols-9 gap-1 max-h-40 overflow-y-auto p-1 rounded-md bg-white/5 border border-white/10">
                    {STICKER_PALETTE.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => {
                          if (stickers.length >= 12) {
                            toast.error("You can add up to 12 stickers per video.");
                            return;
                          }
                          // Drop near the centre with a tiny random offset so they don't all stack.
                          const jx = 0.5 + (Math.random() - 0.5) * 0.3;
                          const jy = 0.5 + (Math.random() - 0.5) * 0.3;
                          const rot = Math.floor((Math.random() - 0.5) * 20);
                          setStickers((arr) => [...arr, { emoji, x: jx, y: jy, size: 48, rot }]);
                        }}
                        data-testid={`plyx-upload-sticker-${emoji}`}
                        className="w-9 h-9 rounded-md hover:bg-white/10 text-2xl flex items-center justify-center"
                        title={`Add ${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  {stickers.length > 0 && (
                    <p className="text-[10px] text-white/40 mt-1.5">Tip: double-click a sticker on the preview to remove it.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs uppercase tracking-wider text-white/60">Caption</label>
                <button
                  type="button"
                  disabled={!caption.trim() || aiBusy === "polish"}
                  onClick={async () => {
                    setAiBusy("polish");
                    try {
                      const r = await plyxApi.post("/plyx/ai/caption-polish", {
                        caption: caption.trim(),
                      });
                      if (r.data?.polished) {
                        setCaption(r.data.polished);
                        toast.success("Caption polished ✨");
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "AI polish failed");
                    } finally { setAiBusy(null); }
                  }}
                  data-testid="plyx-upload-ai-polish"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold text-[#0A0A0A] disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 100%)" }}
                >
                  {aiBusy === "polish" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Wand2 className="w-3 h-3"/>}
                  AI polish
                </button>
              </div>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 2200))}
                rows={4}
                placeholder="Tell people what's in your video…"
                data-testid="plyx-upload-caption"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              />
              <div className="text-xs text-white/40 mt-1">{caption.length}/2200</div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs uppercase tracking-wider text-white/60">Tags (comma-separated)</label>
                <button
                  type="button"
                  disabled={!caption.trim() || aiBusy === "tags"}
                  onClick={async () => {
                    setAiBusy("tags");
                    try {
                      const r = await plyxApi.post("/plyx/ai/hashtags", {
                        caption: caption.trim(),
                      });
                      const tagList = (r.data?.hashtags || []);
                      if (tagList.length) {
                        setTags(tagList.join(", "));
                        toast.success(`AI added ${tagList.length} hashtags`);
                      } else {
                        toast.info("AI returned no hashtags — try a longer caption.");
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "AI hashtag failed");
                    } finally { setAiBusy(null); }
                  }}
                  data-testid="plyx-upload-ai-hashtags"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold text-[#0A0A0A] disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #00E0C7 0%, #FFB42D 100%)" }}
                >
                  {aiBusy === "tags" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Hash className="w-3 h-3"/>}
                  AI suggest
                </button>
              </div>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="ankara, lagos, owambe"
                data-testid="plyx-upload-tags"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              />
            </div>

            {/* Link an AfriMarket listing — hidden in standalone Plyx preview */}
            {!isPlyxStandaloneMode() && (afriUser ? (
              myListings.length > 0 ? (
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5 flex items-center gap-2">
                    <ShoppingBag className="w-3.5 h-3.5"/> Link an AfriMarket listing (optional)
                  </label>
                  <select
                    value={listingId}
                    onChange={(e) => setListingId(e.target.value)}
                    data-testid="plyx-upload-listing-select"
                    className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
                  >
                    <option value="">— No listing —</option>
                    {myListings.map((l) => (
                      <option key={l.listing_id} value={l.listing_id}>{l.title} · {l.currency} {l.price}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="p-3 rounded-md bg-white/5 text-xs text-white/60 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-[#FFB42D]"/>
                  <span>You don&apos;t have any AfriMarket listings yet. Create one to enable &ldquo;Tap to buy&rdquo; on your Plyx videos.</span>
                </div>
              )
            ) : (
              <div className="p-3 rounded-md bg-white/5 text-xs text-white/60">
                Link your AfriMarket account in your Plyx profile to enable &ldquo;Tap to buy&rdquo; on your videos.
              </div>
            ))}

            <button
              type="button"
              disabled={!file || uploading}
              onClick={submit}
              data-testid="plyx-upload-submit"
              className="w-full py-3 rounded-full font-semibold text-[#0A0A0A] disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              {uploading
                ? <><Loader2 className="w-4 h-4 animate-spin"/> Uploading {progress}%…</>
                : <><Upload className="w-4 h-4"/> Post to Plyx</>}
            </button>
            {uploading && (
              <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{ width: `${progress}%`, background: "linear-gradient(90deg, #FF2D7A, #FFB42D, #00E0C7)" }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlyxUpload;
